/**
 * reportFormatters.js — SINGLE SOURCE OF TRUTH for launch-critical display strings.
 *
 * Every surface that renders "missing fish" information (Snapshot, Growth-vs-Model,
 * Spreadsheet Summary & Full, Multi-Profile, Wingman, PDF exports) MUST consume
 * these helpers rather than hand-rolling `${count} fish` templates. This is what
 * guarantees the PRD canonical format lands everywhere atomically:
 *
 *   "Missing: X–Y"     → negative direction, count > guardrail
 *   "Extra: X–Y"       → positive direction, count > guardrail
 *   "Within margin"    → count ≤ guardrail
 *
 * And one canonical confidence-label vocabulary (five values) used across all screens.
 */

// ───────────────────────────────────────────────────────────────────────────
// Canonical confidence labels (PRD: the ONLY five allowed on a confidence badge)
// ───────────────────────────────────────────────────────────────────────────
export const CONFIDENCE_LABELS = Object.freeze({
  HIGH: 'High',
  MODERATE: 'Moderate',
  LOW: 'Low',
  INDICATION_ONLY: 'Indication Only',
  UNCERTAIN: 'Uncertain',
});

/**
 * Map a detection-strength label + reliability score to the canonical five-label vocabulary.
 * Falls through to 'Uncertain' when signals conflict.
 */
export function toCanonicalConfidenceLabel(detectionStrength, reliabilityScore, opts = {}) {
  const { hasConflict = false, isSingleSnapshot = false } = opts;
  // Single-snapshot inputs always resolve to Indication Only — they carry
  // one data point and cannot support a quantitative estimate.
  if (isSingleSnapshot) return CONFIDENCE_LABELS.INDICATION_ONLY;
  // Unresolvable conflict → Uncertain.
  if (hasConflict) return CONFIDENCE_LABELS.UNCERTAIN;
  const s = String(detectionStrength || '').toLowerCase();
  if (s === 'high') return CONFIDENCE_LABELS.HIGH;
  if (s === 'moderate') return CONFIDENCE_LABELS.MODERATE;
  // Low strength WITH low reliability → Indication Only.
  if (s === 'low' && typeof reliabilityScore === 'number' && reliabilityScore < 50) {
    return CONFIDENCE_LABELS.INDICATION_ONLY;
  }
  if (s === 'low') return CONFIDENCE_LABELS.LOW;
  if (s === 'indication only' || s === 'indicative') return CONFIDENCE_LABELS.INDICATION_ONLY;
  if (s === 'uncertain' || s === 'unresolved') return CONFIDENCE_LABELS.UNCERTAIN;
  return CONFIDENCE_LABELS.UNCERTAIN;
}

// ───────────────────────────────────────────────────────────────────────────
// Missing-fish range — attach to the SSOT and render with this helper only.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Compute {lower, upper, midpoint, uncertaintyMargin} bounds from a point estimate.
 *
 * LAUNCH-PHASE RULE (PRD): the baseline band is a flat **±10%** around the
 * expected value. This keeps the "Expected value + Confidence range" wording
 * on every surface identical and predictable (Snapshot, Spreadsheet, Growth,
 * PDF, Wingman, Multi-Profile).
 *
 * FUTURE-READY HOOK: when the call site passes a reliabilityScore lower than
 * the `reliabilityThreshold` (default 50) and `allowWidening` is true, the
 * band expands to communicate the lower trust. The launch build keeps
 * `allowWidening=false` so every surface reports a flat ±10% — a single
 * controllable switch unlocks the adaptive behaviour once product is ready.
 *
 * Rounded to the nearest 100 for display legibility. For very small counts
 * (<1,000) the step drops to 10 so the band still looks sensible.
 */
export function computeMissingFishRange(count, reliabilityScore, opts = {}) {
  const {
    allowWidening = false,
    reliabilityThreshold = 50,
  } = opts;
  const mid = Math.abs(Number(count) || 0);
  if (mid <= 0) {
    return { lower: 0, upper: 0, midpoint: 0, uncertaintyMargin: 0 };
  }
  // Baseline: flat ±10% — Expected ± Range per PRD.
  let margin = 0.10;
  // Adaptive widening for low-confidence call sites that opt in
  // (currently: Snapshot Analysis). For single-snapshot, no-feed-data,
  // no-trend-confirmation runs, customers found ±10% felt overstated —
  // the band now widens to ±15% (reliability 40–50) and ±20% (<40) so
  // the displayed range matches the actual evidence strength.
  if (allowWidening && typeof reliabilityScore === 'number') {
    if (reliabilityScore < reliabilityThreshold - 10) margin = 0.20;
    else if (reliabilityScore < reliabilityThreshold) margin = 0.15;
  }
  const raw_lower = Math.max(0, mid * (1 - margin));
  const raw_upper = mid * (1 + margin);
  const step = mid < 1000 ? 10 : 100;
  const round = (v) => Math.round(v / step) * step;
  return {
    lower: round(raw_lower),
    upper: round(raw_upper),
    midpoint: round(mid),
    uncertaintyMargin: margin,
  };
}

/**
 * Guardrail — below this fish-count the range is reported as "Within margin"
 * instead of a numeric band. Keeps noise out of headlines.
 * A fixed small absolute floor (50 fish) covers every species we currently handle.
 */
export const MISSING_FISH_GUARDRAIL = 50;

/**
 * Format a missing-fish value into the PRD canonical band string.
 *
 * Accepts either:
 *   - an SSOT object (preferred — reads ssot.missingFishCount + ssot.missingFishRange)
 *   - a plain object { count, direction, range }
 *
 * Returns an object so callers can pick headline text, color, and the midpoint:
 *   {
 *     label:          'Missing: 20,400–27,600' | 'Extra: 2,400–3,600' | 'Within margin',
 *     shortLabel:     '20,400–27,600' | '2,400–3,600' | 'Within margin',
 *     direction:      'missing' | 'extra' | 'within_margin',
 *     color:          'red' | 'emerald' | 'slate',
 *     midpointLabel:  '24,000' | '3,000' | '',
 *     isWithinMargin: boolean
 *   }
 */
export function formatMissingFishRange(input) {
  // Accept either an SSOT object or a {count, direction} shape.
  let count = 0;
  let direction = 'missing';
  let reliability = 0;
  let precomputed = null;
  let allowWidening = false;
  if (input && typeof input === 'object') {
    if (input.missingFishRange && typeof input.missingFishRange === 'object') {
      precomputed = input.missingFishRange;
    }
    const ssotCount = input.missingFishCount ?? input.rawMissingFishCount ?? input.count ?? 0;
    count = Number(ssotCount) || 0;
    reliability = Number(input.dataReliabilityScore ?? input.reliabilityScore ?? 50);
    // `allowWidening` is opt-in per call-site (Snapshot passes true so the
    // ±10% baseline widens to ±15-20% when reliability is low — single-
    // snapshot, no-feed runs shouldn't appear precision-grade).
    allowWidening = input.allowWidening === true;
    // Direction: explicit override, else infer from sign.
    if (input.direction) {
      direction = input.direction;
    } else if (count < 0) {
      direction = 'extra';
    } else if (count > 0) {
      direction = 'missing';
    } else {
      direction = 'within_margin';
    }
  } else {
    count = Number(input) || 0;
    direction = count < 0 ? 'extra' : count > 0 ? 'missing' : 'within_margin';
  }

  const absCount = Math.abs(count);

  // Within-margin band.
  if (absCount <= MISSING_FISH_GUARDRAIL) {
    return {
      label: 'Within margin',
      shortLabel: 'Within margin',
      direction: 'within_margin',
      color: 'slate',
      midpointLabel: '',
      isWithinMargin: true,
    };
  }

  const bounds = precomputed || computeMissingFishRange(absCount, reliability, { allowWidening });
  const fmt = (v) => Math.round(v).toLocaleString();
  const bandText = `${fmt(bounds.lower)}–${fmt(bounds.upper)}`;
  const headlineNoun = direction === 'extra' ? 'Extra' : 'Missing';

  return {
    label: `${headlineNoun}: ${bandText}`,
    shortLabel: bandText,
    direction,
    color: direction === 'extra' ? 'emerald' : 'red',
    midpointLabel: fmt(bounds.midpoint),
    isWithinMargin: false,
    // Round-trip the margin so call sites can render an honest "(±X%)" label
    // (Snapshot opts into widening, so this might be 15% or 20% rather than
    // the platform-wide 10% baseline).
    uncertaintyMargin: bounds.uncertaintyMargin,
    uncertaintyMarginPercent: Math.round((bounds.uncertaintyMargin || 0.10) * 100),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Number formatters — thin wrappers so every render site can share one style.
// ───────────────────────────────────────────────────────────────────────────

/** Fish weight in grams with 1 decimal place (PRD uniformity rule). */
export function formatWeightG(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(1)}g`;
}

/** EUR with thousands + always-two-decimal rate display. */
export function formatEurRate(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `€${Number(v).toFixed(2)}`;
}

/** kg with thousands separator + 1 decimal. */
export function formatKg(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`;
}
